import React, { useEffect, useState } from "react";
import { object, func, bool, number } from "prop-types";
import { useSelector } from "react-redux";
import get from "lodash/get";

import { ScrollSnap } from "../../../arrow";
import { SocialShare, ResponsiveImage } from "@quintype/components";
import { SocialShareTemplate } from "../../../arrow/components/Molecules/SocialShareTemplate";
import { ReadMoreToggle, SvgIconHandler } from "../../atoms";
import PopupModal from "../../../components/atoms/PopupModel";
import { useDeviceType } from "../../customHooks/useDeviceType";
import { getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";

import "./photo-story-slider.m.css";

const charLimits = {
  desktop: 240,
  tablet: 160,
  mobile: 55,
};

const TextWithReadMore = ({ textElement, titleElement }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const deviceType = useDeviceType();
  const charLimit = charLimits[deviceType];

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = textElement?.text;
  const plainText = tempDiv.innerText || tempDiv.textContent;

  const shouldShowReadMore = plainText.length > charLimit;
  const truncatedHTML = isExpanded ? textElement.text : plainText.slice(0, charLimit) + "...";

  const toggleDescription = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div styleName={`slider-card-text ${isExpanded ? "expanded" : ""}`}>
      {titleElement && <h4>{titleElement.text}</h4>}
      {textElement && (
        <div styleName="text-container">
          <span dangerouslySetInnerHTML={{ __html: truncatedHTML }} />
          {shouldShowReadMore && (
            <ReadMoreToggle textElement={textElement} isExpanded={isExpanded} onClick={toggleDescription} />
          )}
        </div>
      )}
    </div>
  );
};

TextWithReadMore.propTypes = {
  story: object,
  config: object,
  textElement: object,
  titleElement: object,
};

const SliderCard = ({ card, story, config, cardIndex, totalCount }) => {
  const elements = get(card, ["story-elements"], []);
  const imageElement = elements.find((element) => element.type === "image");
  const titleElement = elements.find((element) => element.type === "title");
  const textElement = elements.find((element) => element.type === "text");

  return (
    <div styleName="slider-card">
      {imageElement && (
        <div styleName="image-template">
          <figure styleName="element">
            <ResponsiveImage
              slug={imageElement["image-s3-key"]}
              metadata={imageElement["image-metadata"]}
              alt={imageElement.title}
              aspectRatio={null}
              defaultWidth={640}
              widths={[360, 640, 1200]}
              imgParams={{ auto: ["format", "compress"], q: 70 }}
              shouldDecodeImage={true}
            />
          </figure>
          <div styleName="image-buttons">
            <div styleName="image-count">
              {cardIndex}/{totalCount}
            </div>
            <div styleName="share-icon">
              <SocialShare
                template={SocialShareTemplate}
                title={story.headline}
                theme={config?.theme}
                fullUrl={encodeURI(`${get(story, ["url"], "")}?cardId=${get(card, ["id"], -1)}`)}
                story={story}
              />
            </div>
          </div>
        </div>
      )}
      <TextWithReadMore textElement={textElement} titleElement={titleElement} />
    </div>
  );
};

SliderCard.propTypes = {
  story: object,
  config: object,
  card: object,
  cardIndex: number,
  totalCount: number,
};

const PhotoStorySlider = ({ story, isSliderOpen, closeSliderView, defaultIndex }) => {
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const config = get(qtState, ["config"], {});

  // Track button clicks for photo maxview
  const trackPhotoMaxviewButtonClick = (buttonType) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const pageType = "story-page";
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, config, story);
      window.dataLayer.push({
        event: "dl_story_interactions",
        event_action: "photo_maxview_button_click",
        link_text: buttonType?.toLowerCase(),
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(story?.["story-template"] || pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  // Handler for close button click
  const handleCloseClick = () => {
    trackPhotoMaxviewButtonClick("Close");
    closeSliderView();
  };

  useEffect(() => {
    if (document) {
      const header = document.getElementById("header");
      header.style.display = isSliderOpen ? "none" : "unset";
    }

    if (isSliderOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }, [isSliderOpen]);

  if (!isSliderOpen) return null;

  return (
    <div styleName="slider-container">
      <PopupModal closePopup={handleCloseClick}>
        <div styleName="slider-wrapper">
          <ScrollSnap
            isArrow
            autoPlay={false}
            slideIndicator="none"
            previousIcon={<SvgIconHandler type="left-arrow" viewBox="0 0 18 18" />}
            nextIcon={<SvgIconHandler type="right-arrow" viewBox="0 0 18 18" />}
            defaultIndex={defaultIndex}
            trackButtonClick={trackPhotoMaxviewButtonClick}
          >
            {story.cards.map((card, index) => (
              <SliderCard card={card} story={story} key={index} cardIndex={index + 1} totalCount={story.cards.length} />
            ))}
          </ScrollSnap>
        </div>
      </PopupModal>
    </div>
  );
};

PhotoStorySlider.propTypes = {
  story: object,
  closeSliderView: func,
  isSliderOpen: bool,
  defaultIndex: number,
};

export default PhotoStorySlider;
